import {
  AccountInfo,
  ProfileAccountInfo,
  ProfileAccountInfoFromServer,
} from "api/interfaces/accountInterface.interface";
import {
  getSwitchAccount,
  getUserDetail,
  getUserDetailAccount,
} from "api/userAPI";
import {
  UserInfo,
  UserGroupUserPermission,
  UserGroupRecorderPermission,
  UserGroupRecorderTypePermission,
} from "api/interfaces/userInterface.interface";
import qs from "qs";
import { cloneDeep, isEmpty } from "lodash";
import { StepType } from "components/blocks/stepper/Stepper";
import { DISCOUNT_TIER } from "api/interfaces/subscription.interface";
import { convertMsToDays, substractTimeFromNow } from "./timeUtil";
import { SubscriptionInfo } from "api/interfaces/recorderInterface.interface";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import {
  LEVEL_TYPE,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import {
  allFormatLevel,
  defaultCheckedUserPermission,
} from "pages/users/userGroup/add/PermissionPolicy";
import { Account, resolveUserInfo } from "api/mappers/accountMapper";
import { setAxiosHeaders } from "./AuthUtil";
import { checkEmpty } from "./functions";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";

export const addOrgSteps: StepType[] = [
  {
    label: "Organization Info",
  },
  {
    label: "Pricing",
  },
];

export const defaultDiscountTier: DISCOUNT_TIER = DISCOUNT_TIER.CUSTOM;

// A discount of 0 is a valid, explicit value - only "not entered" should fail Custom-tier validation.
export const isDiscountEmpty = (discount?: number | null): boolean => {
  return discount === undefined || discount === null;
};

// A pricing field change should clear any stale "discount is required" message
// unless it's still about the discount itself on a Custom tier (creditLimit is
// tier-independent, so it never affects discount validation).
export const shouldClearDiscountValidation = (
  name: string,
  value: DISCOUNT_TIER | number | undefined
): boolean => name !== "creditLimit" && value !== DISCOUNT_TIER.CUSTOM;

export const getAllAccountInfo = () => {
  return new Promise<UserInfo>(function (resolve, reject) {
    getUserDetail()
      .then((response) =>
        resolveUserInfo({ response, resolve: () => resolve(response), reject })
      )
      .catch(function (err) {
        reject(err);
      });
  });
};

export const getAccountInfo = (accountId: string) => {
  return new Promise<ProfileAccountInfoFromServer>(function (resolve, reject) {
    getUserDetailAccount(accountId)
      .then((response) => resolveUserInfo({ response, resolve, reject }))
      .catch(function (err) {
        reject(err);
      });
  });
};

export const getSwitchAccountInfo = (accountId: string, roleId: string) => {
  setAxiosHeaders({
    roleId: roleId as string,
  });
  return new Promise<ProfileAccountInfoFromServer>(async function (
    resolve,
    reject
  ) {
    getSwitchAccount(accountId)
      .then((response) => resolveUserInfo({ response, resolve, reject }))
      .catch(function (err) {
        reject(err);
      });
  });
};

export interface CheckPermissionParamType {
  userPermission: UserGroupUserPermission[];
  accountLevel: LEVEL_TYPE;
}

export const checkOnlyAdminGroup = (permission: UserGroupUserPermission[]) => {
  const check = permission?.filter(
    (permit) =>
      !permit.enableDelete &&
      permit.groupName?.toLowerCase() === "administrator"
  );
  return check?.length !== 0 ? true : false;
};
export const checkedUserAdmin = (permission: UserGroupUserPermission[]) => {
  const check = permission?.filter(
    (permit) => permit.enableAllPermissions === true
  );
  return check.length !== 0 ? true : false;
};

// userGroup 에서 생성하는 user 가 admingroup 에 속하지 않을 때 체크해제로 표시해야함
export const isNotAdminCheckUserPermission = ({
  isAdminGroup,
  userPermission,
}: ProfileAccountInfo): {
  enableAllPermissions: boolean;
  manageUsersAndGroups: boolean;
  viewUsersAndGroups: boolean;
} => {
  if (!isAdminGroup) {
    return {
      enableAllPermissions: false,
      manageUsersAndGroups: false,
      viewUsersAndGroups: false,
    };
  }
  return {
    enableAllPermissions: userPermission.enableAllPermissions,
    manageUsersAndGroups: userPermission.manageUsersAndGroups,
    viewUsersAndGroups: userPermission.viewUsersAndGroups,
  };
};

export const checkPermission = ({
  userPermission,
  accountLevel,
}: CheckPermissionParamType): UserGroupUserPermission => {
  // 기본 권한 정보 가져오기
  const defaultPermissions = {
    ...defaultCheckedUserPermission(checkOnlyAdminGroup(userPermission))[
      accountLevel
    ],
  };
  // const excludeKeys = new Set<keyof UserGroupUserPermission>([
  //   "enableAllPermissions",
  //   "manageUsersAndGroups",
  //   "viewUsersAndGroups",
  // ]);
  // 기본 권한 객체를 그대로 반환할 새로운 객체
  let finalPermissions: UserGroupUserPermission = { ...defaultPermissions };

  // userPermission이 비어있으면 기본 권한 반환
  if (userPermission.length === 0) return finalPermissions;

  // userPermission 배열을 순회하며 권한 업데이트
  Object.keys(defaultPermissions).forEach((key) => {
    const typedKey = key as keyof UserGroupUserPermission;

    // boolean 타입인 경우 처리
    if (typeof defaultPermissions[typedKey] === "boolean") {
      // if (excludeKeys.has(typedKey)) return;

      finalPermissions = {
        ...finalPermissions,
        [typedKey]: userPermission.some(
          (permission) => permission[typedKey] === true
        ),
      };
    }
    // string 타입인 경우 처리
    else if (typeof defaultPermissions[typedKey] === "string") {
      const stringPermission = userPermission.find(
        (permission) => typeof permission[typedKey] === "string"
      );
      finalPermissions = {
        ...finalPermissions,
        [typedKey]: stringPermission?.[typedKey] ?? "",
      };
    }
  });

  return finalPermissions;
};

export const isCheckPermission = (
  requiredPermissions: string,
  selectedAccount: ProfileAccountInfo | ProfileAccountInfoFromServer
) => {
  const hasRequiredPermissions =
    selectedAccount.userPermission[
      requiredPermissions as keyof typeof selectedAccount.userPermission
    ];
  return hasRequiredPermissions as boolean;
};

export const checkGroup = (
  permission: UserGroupUserPermission[],
  group: string
) => {
  const check = permission.filter(
    (permit) =>
      (!permit.enableDelete && permit.groupName?.toLowerCase() === group) ||
      permit.enableAllPermissions
  );
  return check.length !== 0 ? true : false;
};

export const checkRecorderPermission = (
  array: UserGroupRecorderPermission[]
): UserGroupRecorderPermission => {
  return array.reduce(
    (acc, cur) => {
      Object.keys(acc).forEach((key) => {
        const typedKey = key as keyof UserGroupRecorderTypePermission;
        (
          Object.keys(acc[typedKey]) as Array<
            keyof UserGroupRecorderTypePermission[typeof typedKey]
          >
        ).forEach((subKey) => {
          acc[typedKey][subKey] =
            acc[typedKey][subKey] || cur[typedKey][subKey];
        });
      });

      acc.vmax.search = true;

      return acc;
    },
    {
      ...cloneDeep(allFormatLevel({ checkType: "check" })),
    } as UserGroupRecorderPermission
  );
};

export const isHiddenGroup = (array: UserGroupUserPermission[]) => {
  if (array === undefined || array.length > 1) {
    return false;
  } else {
    if (
      array.length === 1 &&
      array[0].groupName?.toUpperCase() === "HIDDEN"
      // array[0].isHidden !== undefined &&
      // array[0].isHidden
    ) {
      return true;
    } else {
      return false;
    }
  }
};

/**
 *
 * @param accounts
 * @returns boolean
 * @description isTrial 과 isSubscription 상태 비교 & isSuspended
 * isTrial 이나 isSubscription 둘 중에 하나가 true 면 메뉴를 보여준다.
 * isSuspended 일 경우에 메뉴를 보여주지 않는다
 *
 * -- isTrial 은 9/1 3개월 제공 후 사라질 예정
 *
 */

export const changeSuspensionStatus = (status: number): SUSPENSION_STATUS => {
  return Object.values(SUSPENSION_STATUS)[status];
};

export const checkIsSuspended = ({
  isSuspended,
  cascadedSuspension = 0,
  accountLevel,
  subscriptionInfo,
  isPayment,
  billingType,
}: {
  isSuspended: number;
  cascadedSuspension?: number;
  accountLevel?: LEVEL_TYPE;
  subscriptionInfo?: SubscriptionInfo;
  isPayment?: boolean;
  billingType?: BILLING_TYPE;
}): SUSPENSION_STATUS => {
  if (cascadedSuspension > 0) {
    return changeSuspensionStatus(cascadedSuspension);
  }

  /**
   * billingType 이 USAGE 일 때
   *  1. isPayment false 인 경우 (CP 가 카드 결제가 안되면 billingType 을 KEYS 로 서버에서 변경해준다)
   *        - subscriptionInfo 조건에 따라 SUSPEND 상태
   *  2. true 인 경우에는 changeSuspensionStatus(isSuspended)
   */

  if (accountLevel === LEVEL_TYPE.EU) {
    if (billingType !== BILLING_TYPE.USAGE || !isPayment) {
      if (getExpiredFromTrialAndChannel({ subscriptionInfo, accountLevel })) {
        return SUSPENSION_STATUS.SUSPENDED;
      }
    }
  }

  return changeSuspensionStatus(isSuspended);
};

export const getExpiredFromTrialAndChannel = ({
  subscriptionInfo,
  accountLevel,
}: {
  accountLevel: LEVEL_TYPE;
  subscriptionInfo?: SubscriptionInfo;
}): boolean => {
  return (
    accountLevel === LEVEL_TYPE.EU &&
    !!subscriptionInfo &&
    !getSubscription({ subscriptionInfo }) &&
    !getSubscriptionTrial({ subscriptionInfo })
  );
};

// CP 가 EU billing Type 컨트롤할 수 있는 함수 boolean
export const getIsBillingTypeDisabled = ({
  isUsagePaid,
  isAdd = false,
  accountInfo,
}: {
  isUsagePaid?: boolean; // CP 기준
  isAdd?: boolean;
  accountInfo?: AccountInfo;
}): boolean => {
  // 1. CP 가 Usage 가 아니면 선택불가능
  if (!isUsagePaid) return true;

  // 2. edit 할때 or accountInfo 정보 없으면 false 로 반환
  if (isAdd || !accountInfo) return false;

  const isExpired = getExpiredFromTrialAndChannel({
    accountLevel: accountInfo.level,
    subscriptionInfo: accountInfo.subscriptionInfo,
  });

  return !isExpired;
};

export const getSpectrumAuthUrl = (loginEmail?: string) => {
  const OATUH_HOST = "https://dwspectrum.digital-watchdog.com/authorize";
  const client_id = "mydw-api";
  const redirect_uri = process.env.REACT_APP_OAUTH_CALLBACK_URL;
  const response_type = "code";
  const email =
    loginEmail !== undefined && !checkEmpty(loginEmail)
      ? loginEmail.toLowerCase()
      : null;

  const AUTHORIZE_URI = `${OATUH_HOST}?${qs.stringify(
    {
      client_id,
      redirect_uri,
      response_type,
      email,
    },
    { skipNulls: true }
  )}`;

  return AUTHORIZE_URI;
};

export const checkIsSubCP = (
  selectedAccount?: ProfileAccountInfo | ProfileAccountInfoFromServer
): boolean => {
  return (
    selectedAccount?.accountLevel === LEVEL_TYPE.CP &&
    selectedAccount?.parentLevel === LEVEL_TYPE.CP
  );
};

export const displayCountOrg = (orgArray: string[], totalCount: number) => {
  if (orgArray === undefined || orgArray.length === 0 || totalCount === 0) {
    return "Not Selected";
  }
  if (orgArray.length === 1) {
    return orgArray[0];
  } else {
    return `${orgArray[0]}+${orgArray.length - 1}`;
  }
};

export const handleDuplicateAccountName = ({
  setFn,
  validateKey,
  elementId,
  message,
}: {
  setFn: (fn: (info: ValidateMsg) => ValidateMsg) => void;
  validateKey: string;
  elementId: string;
  message: string;
}) => {
  setFn((info) => {
    return {
      ...info,
      [validateKey]: message,
    };
  });
  document.getElementById(elementId)?.focus();
};

export const getSubscriptionTrial = ({
  subscriptionInfo,
}: {
  subscriptionInfo?: SubscriptionInfo;
}) => {
  const { trialExpire, isTrial } = subscriptionInfo ?? {};
  return (
    isTrial &&
    trialExpire &&
    convertMsToDays(-substractTimeFromNow(trialExpire)) >= 0
  );
};

export const getSubscription = ({
  subscriptionInfo,
}: {
  subscriptionInfo?: SubscriptionInfo;
}) => {
  const { channelExpire, covaFreeExpire } = subscriptionInfo ?? {};

  return [channelExpire, covaFreeExpire].some((expireDate) => {
    if (!expireDate) {
      return false;
    }

    return convertMsToDays(-substractTimeFromNow(expireDate)) >= 0;
  });
};

export const levelToString = (level: string) => {
  switch (level) {
    case "EU":
      return "End Users";
    case "CP":
      return "Channel Partners";
    case "SUB_CP":
      return "Sub Channel Partners";
    case "DW":
      return "Digital Watchdog";
    default:
      return level;
  }
};

export const checkIsDoubleOrgTree = ({
  accountLevel,
  selectedLevel,
  parentLevel,
  rowKey,
}: {
  accountLevel: LEVEL_TYPE; // 현재 org level
  selectedLevel: LEVEL_TYPE; // expand 대상 level
  parentLevel: LEVEL_TYPE; // expand 대상의 parent level
  rowKey?: string | number; // 실제 테이블 중첩 row key (부모:자식 형태)
}) => {
  const isCpUnderCp =
    accountLevel === LEVEL_TYPE.DW &&
    selectedLevel === LEVEL_TYPE.CP &&
    parentLevel === LEVEL_TYPE.CP;

  if (!isCpUnderCp) return false;
  if (rowKey === undefined || rowKey === null) return true;

  // 검색 결과로 루트에 노출된 SubCP(=CP/CP)는 double indent를 적용하지 않는다.
  return String(rowKey).includes(":");
};

// 우선순위 함수
export const getPriority = (account: Account): number => {
  if (account.level === LEVEL_TYPE.DW) return 1;
  if (account.level === LEVEL_TYPE.CP && account.parentLevel === LEVEL_TYPE.DW)
    return 2;
  if (account.level === LEVEL_TYPE.CP) return 3;
  if (account.level === LEVEL_TYPE.EU) return 4;
  return 5; // 기타
};

export const filterListGetPriority = (
  list: Account[],
  priority: number
): Account[] => {
  return list.filter((account) => getPriority(account) === priority);
};

/** 즐겨찾기 우선 → 우선순위 → 이름 */
export const favoriteThenPriorityThenName = (a: Account, b: Account) => {
  const priorityDiff = getPriority(a) - getPriority(b);
  if (priorityDiff !== 0) return priorityDiff;
  if (a.isFavorite !== b.isFavorite) {
    return a.isFavorite ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
};

const favoriteFirst = (a: Account, b: Account) =>
  a.isFavorite === b.isFavorite ? 0 : a.isFavorite ? -1 : 1;

const byPriority = (a: Account, b: Account) => {
  const priorityDiff = getPriority(a) - getPriority(b);
  return priorityDiff !== 0 ? priorityDiff : a.name.localeCompare(b.name);
};

export function selectAccountsForCollapsedDropdown(
  accounts: Account[],
  max: number
): Account[] {
  //Map이용해서 priority별로 그룹화
  const byPrio = new Map<number, Account[]>();
  accounts.forEach((acc) => {
    const p = getPriority(acc);
    if (!byPrio.has(p)) byPrio.set(p, []);
    byPrio.get(p)!.push(acc);
  });
  //priority별로 즐겨찾기 순으로 정렬
  byPrio.forEach((list) => {
    list.sort(favoriteFirst);
  });

  const picked: Account[] = [];
  const used = new Set<string>();

  //maxCount 이하로 자르되, getPriority 각 구간(1~4)마다 계정이 있으면
  for (let p = 1; p <= 4 && picked.length < max; p++) {
    const group = byPrio.get(p);
    if (group?.length) {
      const one = group[0];
      picked.push(one);
      used.add(one.accountId);
    }
  }

  const rest = accounts
    .filter((a) => !used.has(a.accountId))
    .sort((a, b) => {
      const f = favoriteFirst(a, b);
      if (f !== 0) return f;
      return byPriority(a, b);
    });

  // break가 있어야해서 for문 사용
  for (const acc of rest) {
    if (picked.length >= max) break;
    picked.push(acc);
  }

  return picked.sort((a, b) => {
    const priorityDiff = getPriority(a) - getPriority(b);
    if (priorityDiff !== 0) return priorityDiff;
    const fav = favoriteFirst(a, b);
    if (fav !== 0) return fav;
    return a.name.localeCompare(b.name);
  });
}
